import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './ExpenseSheets.css';
import { getExpenses } from '../../api/expenses';
import { FaPlus, FaEdit, FaEye, FaChevronDown, FaSearch } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useExpenseContext } from './context/ExpenseContext';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const STATUS_MAP = {
  1: 'draft',
  2: 'pending',
  3: 'approved',
  4: 'rejected',
};

/**
 * Least important first — hidden as the table container shrinks.
 * Always kept: status, amount, date, actions.
 */
const COLUMN_PRIORITY = [
  { key: 'expenseType', className: 'col-p3', maxWidth: 900 },
  { key: 'project', className: 'col-p2', maxWidth: 740 },
  { key: 'id', className: 'col-p1', maxWidth: 620 },
];

const formatLocalDate = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`
    : undefined;

const normalizeExpense = (e) => {
  const statusText = STATUS_MAP[e?.status] || String(e?.status ?? '');

  // Project normalization
  let projectStr = '';
  if (typeof e?.project === 'object' && e?.project !== null) {
    projectStr = e.project.projectName || e.project.description || e.project.projectCode || '';
  } else if (typeof e?.project === 'string') {
    projectStr = e.project;
  } else {
    projectStr = e?.projectName || e?.projectDescription || e?.projectCode || '';
  }

  // ExpenseType normalization
  let expenseTypeStr = '';
  if (typeof e?.expenseType === 'object' && e?.expenseType !== null) {
    expenseTypeStr = e.expenseType.expenseTypeName || e.expenseType.name || e.expenseType.expenseTypeCode || '';
  } else if (typeof e?.expenseType === 'string') {
    expenseTypeStr = e.expenseType;
  } else {
    expenseTypeStr = e?.expenseTypeName || e?.expenseTypeCode || '';
  }

  const dateText = e?.expenseDate ?? e?.date ?? '';
  const amountNum = Number(e?.expenseAmount ?? e?.amount ?? 0);

  return {
    id: e?.expenseId ?? e?.id,
    expenseId: e?.expenseId ?? e?.id,
    status: statusText,
    project: String(projectStr),
    expenseType: String(expenseTypeStr),
    amount: Number.isFinite(amountNum) ? amountNum : 0,
    date: dateText,
    expenseDate: e?.expenseDate ?? e?.date ?? '',
    expenseAmount: e?.expenseAmount ?? e?.amount ?? 0,
    description: e?.description ?? '',
    department: e?.department,
    organization: e?.organization,
    user: e?.user,
    approver: e?.approver,
    projectObj: e?.project,
    expenseTypeObj: e?.expenseType,
    _raw: e,
  };
};

const ExpenseSheets = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const orgId = user?.department?.costCenter?.organization?.orgId ?? 1;
  const userId = user?.userId ?? user?.id;

  const {
    expenseFilters,
    setExpenseFilters,
    expenseResults,
    setExpenseResults,
    filteredExpenseResults,
    setFilteredExpenseResults,
    selectedExpense,
    setSelectedExpense,
    expenseCurrentPage,
    setExpenseCurrentPage,
    expenseSortConfig,
    setExpenseSortConfig,
    expenseSearchInputs,
    setExpenseSearchInputs,
  } = useExpenseContext();

  const [startDate, setStartDate] = useState(expenseFilters.startDate);
  const [endDate, setEndDate] = useState(expenseFilters.endDate);
  const [searchVisible, setSearchVisible] = useState({});
  const rowsPerPage = 5;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(expenseResults.length > 0);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState([]);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    setExpenseFilters({ startDate, endDate });
  }, [startDate, endDate, setExpenseFilters]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;

    const syncFromWidth = (width) => {
      const nextHidden = COLUMN_PRIORITY.filter((col) => width <= col.maxWidth).map(
        (col) => col.key
      );
      setHiddenColumnKeys((prev) =>
        prev.length === nextHidden.length && prev.every((key, i) => key === nextHidden[i])
          ? prev
          : nextHidden
      );
    };

    syncFromWidth(el.clientWidth);

    if (typeof ResizeObserver !== 'function') {
      const onResize = () => syncFromWidth(el.clientWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width ?? el.clientWidth;
      syncFromWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const sortedExpenses = useMemo(() => {
    const source = filteredExpenseResults ? filteredExpenseResults : expenseResults;
    const sortableItems = [...source];

    if (expenseSortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a?.[expenseSortConfig.key];
        let bValue = b?.[expenseSortConfig.key];

        if (expenseSortConfig.key === 'amount') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (expenseSortConfig.key === 'date') {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) return expenseSortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return expenseSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    return sortableItems;
  }, [expenseResults, filteredExpenseResults, expenseSortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedExpenses.length / rowsPerPage));
  const safeCurrentPage = Math.min(expenseCurrentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const currentRows = sortedExpenses.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (expenseCurrentPage > totalPages) {
      setExpenseCurrentPage(totalPages);
    }
  }, [expenseCurrentPage, totalPages, setExpenseCurrentPage]);

  const handlePreviousPage = () => {
    if (safeCurrentPage > 1) setExpenseCurrentPage(safeCurrentPage - 1);
  };

  const handleNextPage = () => {
    if (safeCurrentPage < totalPages) setExpenseCurrentPage(safeCurrentPage + 1);
  };

  const handleAdd = () => {
    setSelectedExpense(null);
    navigate('/expenses/new');
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    navigate(`/expenses/edit/${expense.id}`, {
      state: {
        expense,
        from: formatLocalDate(startDate) || null,
        to: formatLocalDate(endDate) || null,
      },
    });
  };

  const handleView = (expense) => {
    setSelectedExpense(expense);
    navigate(`/expenses/edit/${expense.id}?viewOnly=true`, {
      state: {
        expense,
        from: formatLocalDate(startDate) || null,
        to: formatLocalDate(endDate) || null,
      },
    });
  };

  const requestSort = (key) => {
    let direction = 'descending';
    if (expenseSortConfig.key === key && expenseSortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setExpenseSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (expenseSortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }

    const isAsc = expenseSortConfig.direction === 'ascending';
    return <FaChevronDown className={`table-sort-arrow centered${isAsc ? ' up' : ''}`} />;
  };

  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => {
      const allClosed = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleSearchInputChange = (key, value) => {
    setExpenseSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = (expenseSearchInputs[key] || '').trim().toLowerCase();

    if (!value) {
      setFilteredExpenseResults(null);
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
      setExpenseCurrentPage(1);
      return;
    }

    const results = expenseResults.filter((expense) =>
      String(expense?.[key] ?? '').toLowerCase().includes(value)
    );

    setFilteredExpenseResults(results);
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
    setExpenseCurrentPage(1);
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const handleMainSearch = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError(t('expensesList.loadFailed'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const filters = {
        orgId,
        userId,
        from: formatLocalDate(startDate),
        to: formatLocalDate(endDate),
      };

      const data = await getExpenses({ filters });

      const rawExpenses = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];

      const normalized = rawExpenses.map(normalizeExpense);

      setExpenseResults(normalized);
      setFilteredExpenseResults(null);
      setExpenseCurrentPage(1);
      setHasFetched(true);
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      if (status) {
        setError(t('expensesList.searchFailedStatus', { status }));
      } else {
        setError(t('expensesList.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [
    userId,
    orgId,
    startDate,
    endDate,
    t,
    setExpenseResults,
    setFilteredExpenseResults,
    setExpenseCurrentPage,
  ]);

  // Auto-load on first open (and when auth becomes ready). Keep cached rows on revisit.
  useEffect(() => {
    if (!userId) return;
    if (hasFetched) {
      setLoading(false);
      return;
    }
    void handleMainSearch();
  }, [userId, hasFetched, handleMainSearch]);

  const hiddenColumns = COLUMN_PRIORITY.filter((col) =>
    hiddenColumnKeys.includes(col.key)
  );
  const tableClassName = [
    'expense-table',
    ...hiddenColumns.map((col) => `hide-${col.className}`),
  ].join(' ');

  const renderSortableHeader = (key, titleKey, extraClass = '') => (
    <th className={`expense-table-header sortable ${extraClass}`.trim()}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span
          onClick={() => requestSort(key)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          {t(`expensesList.columns.${titleKey}`)} {getSortIcon(key)}
        </span>

        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={expenseSearchInputs[key] || ''}
                autoFocus
                onChange={(e) => handleSearchInputChange(key, e.target.value)}
                onKeyDown={(e) => handleSearchInputKeyDown(key, e)}
                placeholder={t('expensesList.searchPlaceholder', {
                  field: t(`expensesList.columns.${titleKey}`),
                })}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}

          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  return (
    <div className="expense-sheets-container">
      {error && <div className="expense-table-error">{error}</div>}

      <div className="expense-sheets-header">
        <h1 className="expense-sheets-title">{t('expensesList.title')}</h1>
      </div>

      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-dates-row">
              <div className="filter-group">
                <label className="filter-label">{t('expensesList.from')}</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText={t('expensesList.selectStartDate')}
                  className="date-input"
                />
              </div>

              <div className="filter-group">
                <label className="filter-label filter-label-to">{t('expensesList.to')}</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText={t('expensesList.selectEndDate')}
                  className="date-input"
                />
              </div>
            </div>

            <button className="apply-filter-button" onClick={handleMainSearch} disabled={loading}>
              {t('expensesList.search')}
            </button>
          </div>
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <FaPlus />
            {t('expensesList.addExpense')}
          </button>
        </div>
      </div>

      <div className="expense-table-container" ref={tableContainerRef}>
        {loading ? (
          <div className="expense-table-loading">{t('expensesList.loading')}</div>
        ) : (
          <table className={tableClassName}>
            <thead>
              <tr>
                {renderSortableHeader('status', 'status')}
                {renderSortableHeader('id', 'id', 'col-p1')}
                {renderSortableHeader('project', 'project', 'col-p2')}
                {renderSortableHeader('expenseType', 'expenseType', 'col-p3')}
                {renderSortableHeader('amount', 'amount')}
                {renderSortableHeader('date', 'expenseDate')}
                <th className="expense-table-header">{t('expensesList.columns.actions')}</th>
              </tr>
            </thead>

            <tbody>
              {currentRows.length === 0 ? (
                <tr>
                  <td className="expense-table-cell" colSpan={7} style={{ textAlign: 'center' }}>
                    {t('expensesList.noData')}
                  </td>
                </tr>
              ) : (
                currentRows.map((expense) => {
                  const statusText = String(expense?.status ?? '').toLowerCase();
                  const canEdit = statusText === 'draft' || statusText === 'rejected';
                  const canView = statusText === 'pending' || statusText === 'approved';

                  return (
                    <tr key={expense.id}>
                      <td className="expense-table-cell">
                        <span className={`expense-status-badge ${statusText}`}>
                          {statusText
                            ? statusText.charAt(0).toUpperCase() + statusText.slice(1)
                            : '-'}
                        </span>
                      </td>

                      <td className="expense-table-cell col-p1">{expense.id ?? '-'}</td>
                      <td className="expense-table-cell col-p2">
                        {typeof expense.project === 'object' ? '' : expense.project || '-'}
                      </td>
                      <td className="expense-table-cell col-p3">
                        {typeof expense.expenseType === 'object' ? '' : expense.expenseType || '-'}
                      </td>

                      <td className="expense-table-cell">
                        <span className="expense-amount">
                          ${Number(expense.amount || 0).toFixed(2)}
                        </span>
                      </td>

                      <td className="expense-table-cell">{expense.date || '-'}</td>

                      <td className="expense-table-cell actions-cell">
                        <div className="actions-cell-inner">
                          <div className="actions-inline">
                            {canEdit && (
                              <button
                                className="expense-action-button edit"
                                onClick={() => handleEdit(expense)}
                              >
                                <FaEdit />
                                {t('timesheets.edit')}
                              </button>
                            )}
                            {canView && (
                              <button
                                className="expense-action-button edit"
                                onClick={() => handleView(expense)}
                              >
                                <FaEye />
                                {t('timesheets.view')}
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <button
          onClick={handlePreviousPage}
          disabled={safeCurrentPage === 1}
          className="pagination-arrow"
          aria-label={t('expensesList.previous')}
        >
          &lt;
        </button>

        <input
          type="number"
          min={1}
          max={totalPages}
          value={safeCurrentPage}
          onChange={(e) => {
            let page = Number(e.target.value);
            if (!Number.isFinite(page)) page = 1;
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setExpenseCurrentPage(page);
          }}
          className="pagination-input"
        />

        <span className="pagination-total">{t('expensesList.of')} {totalPages}</span>

        <button
          onClick={handleNextPage}
          disabled={safeCurrentPage === totalPages}
          className="pagination-arrow"
          aria-label={t('expensesList.next')}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default ExpenseSheets;